<template>
  <!-- `bottom end`: the menu hangs from the trigger's right edge. It is wider than its trigger,
       and the bar sits at the right of the header: hung from the left edge, the last menus ran
       into the window's edge and Vuetify flipped them, so a menu opened in a different place
       depending on a few pixels of room (#1163). Growing leftwards, it always has room. -->
  <v-menu v-model="open" location="bottom end" :offset="4" :close-on-content-click="false">
    <template #activator="{ props: activatorProps }">
      <button
        v-bind="activatorProps"
        type="button"
        class="ctx-dd-trigger"
        :class="{ 'ctx-dd-trigger--highlight': highlight }"
        :disabled="loading"
        :aria-label="menuLabel"
        :title="selectedLabel ?? undefined"
      >
        <span class="ctx-dd-trigger__text" :class="{ 'ctx-placeholder': !selectedLabel }">
          {{ selectedLabel ?? placeholder }}
        </span>
      </button>
    </template>

    <v-list class="ctx-dd-menu" density="compact" role="listbox" :aria-label="menuLabel"
      :aria-multiselectable="multiple || undefined">
      <v-list-item
        class="ctx-dd-item"
        role="option"
        :aria-selected="noneSelected"
        @click="pick(null, false)"
      >
        <v-list-item-title class="ctx-dd-item__label ctx-placeholder">{{ placeholder }}</v-list-item-title>
      </v-list-item>

      <template v-for="(section, sectionIndex) in sections" :key="sectionIndex">
        <v-list-subheader v-if="section.label" class="ctx-dd-subheader">{{ section.label }}</v-list-subheader>
        <!-- No `:title` on v-list-item: Vuetify treats it as the item's TITLE PROP and
             renders it as content. The native tooltip goes on the inner label instead. -->
        <v-list-item
          v-for="item in section.items"
          :key="item.id"
          class="ctx-dd-item"
          role="option"
          :class="{
            'ctx-dd-item--disabled': item.disabled,
            'ctx-dd-item--selected': isSelected(item.id),
            'ctx-dd-item--scope': item.scope,
          }"
          :aria-selected="isSelected(item.id)"
          :aria-disabled="item.disabled || undefined"
          @click="pick(item.id, item.disabled)"
        >
          <v-list-item-title
            class="ctx-dd-item__label"
            :class="{ 'ctx-dd-item__label--pinned': item.pinned }"
            :title="item.disabled ? item.disabledReason : undefined"
          >
            {{ item.label }}
          </v-list-item-title>
          <template #append>
            <!-- Laid out on every row, shown on the checked ones: a mark appearing on the widest
                 row widened the open menu, which then shifted under the pointer. -->
            <font-awesome-icon v-if="multiple" icon="check" class="ctx-dd-item__check"
              :class="{ 'ctx-dd-item__check--off': !isSelected(item.id) }" />
            <font-awesome-icon v-else-if="item.pinned" icon="thumbtack" class="ctx-dd-item__pin" />
          </template>
        </v-list-item>
      </template>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ContextMenuSection } from '@/composables/context-menu-items';
import { toggleKey } from '@/components/context-dropdown-selection';

/**
 * Compact dropdown for the context bar (#838). Replaces the native <select>: options
 * disabled by the context stay hoverable (so their tooltip can say WHY), pin-compatible
 * options carry a real thumbtack whose colour follows the theme, and the styling works
 * in every browser — none of which a native <option> can do.
 *
 * Disabled options deliberately avoid Vuetify's `disabled` prop: it sets
 * pointer-events: none, which would kill the explanatory tooltip. The click handler
 * guards instead, and `close-on-content-click` is off so a click on a disabled option
 * does not silently dismiss the menu.
 *
 * `multiple` (#1163) turns it into a multi-select over `modelValues`: a click toggles the row,
 * applies at once and keeps the menu open, a selected row carries a check mark, and the
 * placeholder row clears everything. The single mode renders exactly as before.
 */

const props = withDefaults(defineProps<{
  modelValue?: string | null;
  /** Label of the current selection, rendered on the trigger. Null shows the placeholder. */
  selectedLabel: string | null;
  placeholder: string;
  menuLabel: string;
  sections: ContextMenuSection[];
  loading?: boolean;
  highlight?: boolean;
  multiple?: boolean;
  /** The selection in `multiple` mode. */
  modelValues?: string[];
}>(), {
  modelValue: null,
  multiple: false,
  modelValues: () => [],
});

const emit = defineEmits<{
  'update:modelValue': [id: string | null];
  'update:modelValues': [ids: string[]];
}>();

const open = ref(false);

const noneSelected = computed(() => (props.multiple ? props.modelValues.length === 0 : props.modelValue === null));

function isSelected(id: string): boolean {
  return props.multiple ? props.modelValues.includes(id) : id === props.modelValue;
}

function pick(id: string | null, disabled: boolean) {
  if (disabled) return;
  if (props.multiple) {
    if (id === null) {
      open.value = false;
      emit('update:modelValues', []);
      return;
    }
    // The menu stays open: one usually checks several rows in a row, and each check applies at
    // once — the Présences sheet only re-filters the rows it already holds, with no round trip.
    emit('update:modelValues', toggleKey(props.modelValues, id));
    return;
  }
  open.value = false;
  emit('update:modelValue', id);
}
</script>

<style scoped>
/* The trigger carries the exact box the native .ctx-select had (context-toolbar.vue),
   so the bar's geometry does not move: same height, chevron, radius, highlight pulse. */
.ctx-dd-trigger {
  display: inline-flex;
  align-items: center;
  text-align: left;
  height: 24px;
  /* Room on the right for the chevron drawn via background-image. */
  padding: 0 22px 0 9px;
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
  /* Down-chevron so the control reads as a dropdown. */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235b7089' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  /* A width of its own, not its label's (#1163): a longer name used to widen the trigger by
     up to 50 px, which shifted the bar under the pointer and, near the header's limit, sent
     it onto a second row, moving the page. Short of room it shrinks, all menus evenly. */
  flex: 0 1 140px;
  width: 140px;
  min-width: 72px;
  appearance: none;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}

.ctx-dd-trigger:hover:not(:disabled) {
  border-color: rgba(var(--v-theme-primary), 0.4);
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.ctx-dd-trigger:focus-visible {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.15);
}

.ctx-dd-trigger:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.ctx-dd-trigger--highlight {
  animation: ctx-dd-pulse 1.2s ease-in-out infinite;
}

@keyframes ctx-dd-pulse {

  0%,
  100% {
    border-color: rgba(var(--v-theme-primary), 0.22);
    box-shadow: none;
    background-color: rgb(var(--v-theme-surface));
  }

  50% {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.35), 0 0 10px rgba(var(--v-theme-primary), 0.25);
    background-color: rgba(var(--v-theme-primary), 0.1);
  }
}

/* Below the md breakpoint the toolbar stacks its controls full-width (see
   context-toolbar.vue's media block, which sizes .ctx-item). */
@media (max-width: 959px) {
  .ctx-dd-trigger {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
    width: 100%;
    height: 34px;
    font-size: 14px;
  }
}

.ctx-dd-trigger__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* The italic placeholder's last glyph overhangs its box and overflow:hidden
     truncates it; a sliver of padding keeps the slant inside. */
  padding-right: 3px;
}

.ctx-dd-menu {
  min-width: 190px;
  max-width: 300px;
  max-height: min(420px, 70vh);
  overflow-y: auto;
  padding: 4px;
  border: 1px solid rgba(var(--v-theme-primary), 0.16);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20, 55, 90, 0.12);
  scrollbar-color: rgba(var(--v-theme-primary), 0.32) transparent;
  scrollbar-width: thin;
}

.ctx-dd-menu :deep(.v-list-item) {
  min-height: 28px;
  padding-inline: 10px;
  border-radius: 6px;
}

.ctx-dd-subheader {
  min-height: 24px;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-muted);
}

.ctx-dd-item__label {
  font-size: 12px;
  font-weight: 500;
}

/* Compatible with the active pin: label and thumbtack share the primary blue. */
.ctx-dd-item__label--pinned {
  color: rgb(var(--v-theme-primary));
}

.ctx-dd-item__pin {
  font-size: 10px;
  color: rgb(var(--v-theme-primary));
}

/* Multi-select mode: the check mark of a selected row, in the pin's place and colour. */
.ctx-dd-item__check {
  margin-inline-start: 8px;
  font-size: 10px;
  color: rgb(var(--v-theme-primary));
}

.ctx-dd-item__check--off {
  visibility: hidden;
}

.ctx-dd-item--selected {
  background: rgba(var(--v-theme-primary), 0.1);
}

/* A family entry — « Tous les programmes », « Groupes », « Individualisés » (#948). It stands
   for every line under it, so it must not look like one of them: tinted ground, heavier label.
   These entries took the place of the section subheaders, which were not clickable; keeping the
   subheader's weight is what makes the menu read the same way it did. */
.ctx-dd-item--scope {
  background: rgba(var(--v-theme-primary), 0.06);
  margin-top: 2px;
}

.ctx-dd-item--scope:first-child {
  margin-top: 0;
}

.ctx-dd-item--scope .ctx-dd-item__label {
  font-weight: 700;
}

/* The selection wins over the family tint: one has to be able to see which is chosen. */
.ctx-dd-item--scope.ctx-dd-item--selected {
  background: rgba(var(--v-theme-primary), 0.18);
}

.ctx-dd-item--disabled {
  cursor: default;
}

.ctx-dd-item--disabled :deep(.v-list-item-title) {
  color: var(--color-text-faint);
  font-style: italic;
}

.ctx-placeholder {
  font-style: italic;
  color: var(--color-text-faint);
}
</style>
